/**
 * Consent-gated GA4 / dataLayer event tracker.
 *
 * Behavior:
 *   - analytics consent = granted → fire immediately via gtag('event', ...)
 *   - analytics consent = denied  → drop the event silently
 *   - consent unknown (banner not yet acknowledged) → queue the event
 *
 * The queue is flushed (or cleared) by `flushTrackingQueue()`, which the
 * CookieConsentProvider calls after the user makes a choice. Params are
 * reduced to a strict whitelist at dispatch; no free text or URLs.
 */

import { readStoredConsent } from "./consent";
import { FUNNEL_IDS } from "@shared/marketing";

type EventParams = Record<string, string | number | boolean | undefined | null>;
interface QueuedEvent {
  name: string;
  params: EventParams;
}

const queue: QueuedEvent[] = [];
const QUEUE_LIMIT = 50;

function getAnalyticsConsent(): boolean | null {
  const stored = readStoredConsent();
  if (!stored) return null;
  return stored.prefs.analytics === true;
}

function dispatchToDataLayer(name: string, params: EventParams): void {
  if (typeof window === "undefined") return;
  if (!window.dataLayer) window.dataLayer = [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtagShim(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
  // GA4 expects a flat event payload. We strip undefined/null so they
  // don't pollute reports as the literal string "undefined".
  const clean: Record<string, string | number | boolean> = {};
  // Positive allowlist: callers cannot accidentally send an email, phone,
  // free-text field, URL (possibly containing PII) or click ID to GA/GTM.
  const safeStrings: Record<string, readonly string[]> = {
    funnel: FUNNEL_IDS,
    form: ["contatti"],
    form_id: ["quote"],
    currency: ["EUR"],
  };
  const safeNumbers = new Set(["value", "step"]);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && !safeStrings[k]?.includes(v)) continue;
    if (typeof v === "number" && (!safeNumbers.has(k) || !Number.isFinite(v) || v < 0)) continue;
    if (typeof v === "boolean") continue;
    clean[k] = v;
  }
  // Two pushes by design (mirrors the pattern in lib/consent.ts):
  //   1. gtag('event', ...) → standard GA4 dispatch
  //   2. dataLayer.push({event, ...}) → lets a GTM container (or any
  //      dataLayer listener) react to the same event without coupling
  //      to gtag's arguments-array shape. The project may load either
  //      GTM, GA4, or both; this keeps tracking working in all cases.
  window.gtag("event", name, clean);
  window.dataLayer.push({ event: name, ...clean });
}

export function trackEvent(name: string, params: EventParams = {}): void {
  if (typeof window === "undefined") return;
  const consent = getAnalyticsConsent();
  if (consent === true) {
    dispatchToDataLayer(name, params);
    return;
  }
  if (consent === false) return;
  if (queue.length >= QUEUE_LIMIT) queue.shift();
  queue.push({ name, params });
}

/**
 * Called by CookieConsentProvider after the user saves preferences.
 *  - granted → flush all queued events
 *  - denied  → drop them
 *  - unknown → leave them queued (shouldn't happen post-save)
 */
export function flushTrackingQueue(): void {
  const consent = getAnalyticsConsent();
  if (consent === true) {
    while (queue.length) {
      const e = queue.shift()!;
      dispatchToDataLayer(e.name, e.params);
    }
  } else if (consent === false) {
    queue.length = 0;
  }
}
